import React from 'react'
import ServicePage, { SplitSection, StackRibbon } from '../ServicePage'

const DevOpsHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="d-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0066ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <radialGradient id="d-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>

    <circle cx="230" cy="230" r="200" fill="url(#d-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* infinity / pipeline track */}
    <path
      d="M120 230 C120 170, 200 170, 230 230 C260 290, 340 290, 340 230 C340 170, 260 170, 230 230 C200 290, 120 290, 120 230 Z"
      fill="none"
      stroke="rgba(0,212,255,0.35)"
      strokeWidth="3"
    />

    {/* moving dot along pipeline */}
    <circle r="9" fill="url(#d-grad)">
      <animateMotion
        dur="6s"
        repeatCount="indefinite"
        path="M120 230 C120 170, 200 170, 230 230 C260 290, 340 290, 340 230 C340 170, 260 170, 230 230 C200 290, 120 290, 120 230 Z"
      />
    </circle>

    {/* stages */}
    <g fontFamily="JetBrains Mono, monospace" fontSize="11" fill="rgba(255,255,255,0.7)">
      <g><circle cx="160" cy="180" r="6" fill="#00c851" /><text x="160" y="158" textAnchor="middle">BUILD</text></g>
      <g><circle cx="230" cy="230" r="6" fill="#00c851" /><text x="230" y="208" textAnchor="middle">TEST</text></g>
      <g><circle cx="300" cy="180" r="6" fill="#00c851" /><text x="300" y="158" textAnchor="middle">SCAN</text></g>
      <g><circle cx="300" cy="280" r="6" fill="#ffbb33" /><text x="300" y="305" textAnchor="middle">DEPLOY</text></g>
      <g><circle cx="160" cy="280" r="6" fill="rgba(255,255,255,0.3)" /><text x="160" y="305" textAnchor="middle">RELEASE</text></g>
    </g>

    {/* floating gear */}
    <g>
      <circle cx="80" cy="100" r="22" fill="rgba(255,255,255,0.06)" stroke="rgba(0,212,255,0.5)" />
      <g transform="translate(80 100)">
        <circle r="6" fill="none" stroke="#00d4ff" strokeWidth="2" />
        <line x1="0" y1="-12" x2="0" y2="-15" stroke="#00d4ff" strokeWidth="2" />
        <line x1="0" y1="12"  x2="0" y2="15"  stroke="#00d4ff" strokeWidth="2" />
        <line x1="-12" y1="0" x2="-15" y2="0" stroke="#00d4ff" strokeWidth="2" />
        <line x1="12"  y1="0" x2="15"  y2="0" stroke="#00d4ff" strokeWidth="2" />
        <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="6s" repeatCount="indefinite" />
      </g>
    </g>
  </svg>
)

const DevOpsCICD = () => (
  <ServicePage
    slug="devops-cicd"
    seo={{
      title: 'DevOps & CI/CD Support',
      description:
        'CI/CD support from QA Tech — automated test pipelines, build and deployment workflows, and observability that complement our core software testing, web and app development services.',
      keywords:
        'CI/CD pipelines, continuous integration, continuous deployment, test automation pipelines, GitHub Actions, GitLab CI, Jenkins, build automation, deployment automation, software delivery',
    }}
    hero={{
      eyebrow: 'Service / DevOps & CI/CD',
      title: 'CI/CD that supports your delivery — not the whole platform.',
      lede:
        'Our core is software testing, web and app development. We pair that work with the CI/CD and deployment plumbing it needs to ship safely — clean pipelines, automated tests on every push, and predictable releases.',
    }}
    visual={<DevOpsHero />}
  >
    <SplitSection
      index={1}
      align="left"
      kicker="What we do"
      title="The pipeline around the product we're building."
    >
      <p>
        We&rsquo;re not a standalone DevOps consultancy &mdash; our delivery
        teams set up and run the CI/CD their projects need so testing, build
        and deployment all hang together. If you&rsquo;re working with us on
        a web app, mobile app or testing engagement, this is the layer that
        keeps it shipping.
      </p>
      <ul>
        <li>Build &amp; test pipelines for the projects we deliver</li>
        <li>Automated test execution wired into CI</li>
        <li>Deployment workflows for staging &amp; production</li>
        <li>Light-touch infrastructure setup where the project requires it</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={2}
      align="right"
      kicker="Pipelines"
      title="From commit to deployable artifact, automated."
      accent={<span className="sp-accent-code">{'$ git push origin main'}</span>}
    >
      <p>
        We design CI/CD pipelines that build, lint, test and produce a
        deployable artifact on every push. Stages run in parallel where it
        makes sense, caches stay warm, and the feedback loop on a failed
        build is fast enough that engineers actually fix things instead of
        ignoring red.
      </p>
      <ul>
        <li>GitHub Actions, GitLab CI, CircleCI, Jenkins</li>
        <li>Reusable composite actions &amp; pipeline templates</li>
        <li>Branching strategies that fit the team &amp; product</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={3}
      align="left"
      kicker="Test Automation in CI"
      title="Tests that run every push, not every quarter."
      accent={
        <div className="sp-accent-bars" aria-hidden="true">
          <span /><span /><span /><span /><span />
        </div>
      }
    >
      <p>
        This is where our DevOps work meets our core: getting the unit,
        integration, end-to-end and accessibility tests we write to run
        reliably in CI on every commit. That means consistent containers,
        sane parallelisation, useful failure reports and stable test data
        &mdash; not flaky pipelines that get muted at 5pm on a Friday.
      </p>
    </SplitSection>

    <SplitSection
      index={4}
      align="right"
      kicker="Deployment"
      title="Predictable releases, fewer surprises."
    >
      <p>
        Staging and production deployment workflows with sensible safeguards:
        manual approvals where they belong, automatic where they don&rsquo;t,
        rollback paths that actually work, and feature flags for the riskier
        changes. We aim for boring releases.
      </p>
    </SplitSection>

    <SplitSection
      index={5}
      align="left"
      kicker="Observability basics"
      title="Know it broke before the support inbox does."
    >
      <p>
        Logging, error tracking and basic uptime / performance monitoring
        wired up at launch so the team isn&rsquo;t debugging blind. We work
        with whichever stack you already use &mdash; or recommend a sensible
        starter set if you don&rsquo;t have one yet.
      </p>
      <ul>
        <li>Sentry, Datadog, Grafana, OpenTelemetry</li>
        <li>Health checks &amp; uptime monitoring</li>
        <li>Alerts on user-impact, not raw infrastructure metrics</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={6}
      align="right"
      kicker="Honest scope"
      title="What we don't claim to be."
    >
      <p>
        We&rsquo;re not a hyperscale platform team. If you need a
        dedicated DevOps engagement &mdash; large Kubernetes platforms,
        regulated-industry compliance programmes, full FinOps reviews &mdash;
        we&rsquo;ll happily say so and point you to specialists. What we do
        well is the CI/CD and deployment layer that makes the products
        we&rsquo;re building (or testing) ship cleanly.
      </p>
    </SplitSection>

    <StackRibbon
      label="Tooling we work with"
      items={[
        'GitHub Actions', 'GitLab CI', 'CircleCI', 'Jenkins',
        'Docker', 'Helm',
        'AWS', 'Azure', 'GCP',
        'Sentry', 'Datadog', 'Grafana', 'OpenTelemetry',
      ]}
    />
  </ServicePage>
)

export default DevOpsCICD
